import React from "react";

export type ButtonVariant = "primary" | "secondary" | "destructive" | "ghost" | "active" | "warning";
export type ButtonSize = "sm" | "md" | "lg";

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
    variant?: ButtonVariant;
    size?: ButtonSize;
    leftIcon?: React.ComponentType<{ className?: string }>;
    rightIcon?: React.ComponentType<{ className?: string }>;
    fullWidth?: boolean;
    children?: React.ReactNode;
}

const VARIANT_CLASSES: Record<ButtonVariant, string> = {
    primary: "bg-brand-orange text-white shadow-card hover:bg-brand-orange/90 disabled:opacity-50 disabled:cursor-not-allowed",
    secondary: "border border-brand-line text-brand-ink bg-white hover:bg-brand-cream disabled:opacity-50 disabled:cursor-not-allowed",
    destructive: "border border-red-200 text-red-600 bg-red-50 hover:bg-red-100 disabled:opacity-50 disabled:cursor-not-allowed",
    ghost: "text-brand-ink/70 hover:text-brand-ink hover:bg-brand-cream disabled:opacity-50 disabled:cursor-not-allowed",
    active: "bg-brand-ink text-white border border-brand-ink hover:bg-brand-ink/90 disabled:opacity-50 disabled:cursor-not-allowed",
    warning: "bg-amber-500 text-white shadow-card hover:bg-amber-600 disabled:opacity-50 disabled:cursor-not-allowed",
};

const SIZE_CLASSES: Record<ButtonSize, string> = {
    sm: "px-3 py-1.5 text-xs rounded-full",
    md: "px-4 py-2 text-sm rounded-2xl",
    lg: "px-5 py-2.5 text-sm rounded-xl",
};

const ICON_SIZE_CLASSES: Record<ButtonSize, string> = {
    sm: "h-3.5 w-3.5",
    md: "h-4 w-4",
    lg: "h-5 w-5",
};

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
    ({ variant = "primary", size = "md", leftIcon: LeftIcon, rightIcon: RightIcon, fullWidth = false, className = "", children, type = "button", ...props }, ref) => {
        const iconClass = ICON_SIZE_CLASSES[size];
        const widthClass = fullWidth ? "w-full" : "";

        return (
            <button
                ref={ref}
                type={type}
                className={`inline-flex items-center justify-center gap-1.5 font-semibold transition-colors duration-150 ${VARIANT_CLASSES[variant]} ${SIZE_CLASSES[size]} ${widthClass} ${className}`}
                {...props}
            >
                {LeftIcon && <LeftIcon className={iconClass} />}
                {children}
                {RightIcon && <RightIcon className={iconClass} />}
            </button>
        );
    },
);

Button.displayName = "Button";

export { Button };
export default Button;
